import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, BarChart3, FileText, Handshake, TrendingUp, Users } from 'lucide-react';

const PreviewOurMethod = () => {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const methodSteps = [
    {
      number: '01',
      icon: Users,
      title: 'Listen & Understand',
      description: 'We start by listening to your vision, goals, and requirements. Understanding your unique needs is the foundation of our approach. We take the time to learn about your business, budget, timeline, and long-term objectives to ensure we\'re perfectly aligned with your vision.',
      features: ['Personal consultation', 'Goals assessment', 'Budget planning', 'Timeline development']
    },
    {
      number: '02',
      icon: BarChart3,
      title: 'Research & Analyze',
      description: 'We conduct comprehensive market research, demographic analysis, and competitive assessments. Our deep knowledge of North America\'s real estate landscape allows us to identify opportunities, evaluate options, and develop strategic recommendations tailored to your specific situation.',
      features: ['Market research', 'Demographic analysis', 'Competitive assessment', 'Strategic recommendations']
    },
    {
      number: '03',
      icon: Handshake,
      title: 'Negotiate & Execute',
      description: 'Armed with market intelligence and strategic insights, we negotiate on your behalf to secure the best possible terms. We handle letters of intent, offers to lease, purchase agreements, and all documentation, ensuring your interests are protected at every step while maximizing value and minimizing risk.',
      features: ['Expert negotiation', 'Document preparation', 'Interest protection', 'Value maximization']
    },
    {
      number: '04',
      icon: TrendingUp,
      title: 'Support & Follow Through',
      description: 'Our commitment doesn\'t end at closing. We stay by your side to ensure smooth transitions, address any post-closing matters, and provide ongoing support. We\'re invested in your long-term success and remain available as your trusted real estate advisor for future needs.',
      features: ['Smooth transitions', 'Post-closing support', 'Long-term partnership', 'Ongoing advisory']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1746021375306-9dec0f637732?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt="Modern Medical Clinic Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-spire-green/90 to-spire-purple/80"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center z-10">
          <div className="animate-fade-in-up">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our Method
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              A proven, tailored approach to healthcare real estate consulting
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div 
            id="intro"
            data-animate
            className={`text-center mb-20 ${isVisible['intro'] ? 'animate-fade-in-up' : 'opacity-0'}`}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Specialized Expertise for Healthcare Professionals</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              We understand that healthcare real estate has unique requirements and challenges. Our method combines strategic planning, market expertise, and personalized support to ensure your success.
            </p>
          </div>

          {/* Method Steps */}
          <div className="space-y-16">
            {methodSteps.map((step, index) => (
              <div 
                key={index}
                id={`step-${index}`}
                data-animate
                className={`${isVisible[`step-${index}`] ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  {/* Step Number & Icon */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="text-8xl font-bold text-gray-100">{step.number}</div>
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-2xl bg-gradient-to-br from-spire-purple to-spire-green flex items-center justify-center shadow-xl">
                        <step.icon className="text-white" size={32} />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-lg text-gray-700 leading-relaxed mb-6">{step.description}</p>
                    
                    {/* Features */}
                    <div className="grid grid-cols-2 gap-3">
                      {step.features.map((feature, i) => (
                        <div key={i} className="flex items-center text-gray-600">
                          <div className="w-2 h-2 rounded-full bg-spire-purple mr-3"></div>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                {index < methodSteps.length - 1 && (
                  <div className="mt-16 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Can Expect - Deliverables */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div 
            id="deliverables"
            data-animate
            className={`text-center mb-16 ${isVisible['deliverables'] ? 'animate-fade-in-up' : 'opacity-0'}`}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">What You Can Expect</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive support and documentation throughout your real estate journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Documentation',
                items: ['Letters of intent', 'Offers to lease', 'Lease agreements - new & renewal', 'Purchase and sale agreements']
              },
              {
                title: 'Analysis & Strategy',
                items: ['Market and demographic analysis', 'Site selection and acquisition', 'Real estate strategy development', 'Feasibility studies']
              },
              {
                title: 'Ongoing Support',
                items: ['Construction support', 'Build-out coordination', 'Regulatory compliance guidance', 'Post-closing follow-up']
              }
            ].map((item, index) => (
              <div 
                key={index}
                id={`deliver-${index}`}
                data-animate
                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${
                  isVisible[`deliver-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{item.title}</h3>
                <ul className="space-y-3 text-gray-600">
                  {item.items.map((listItem, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-spire-purple mr-2">•</span>
                      <span>{listItem}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-spire-purple to-purple-900 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Experience the Spire Group Difference</h2>
          <p className="text-xl text-white/90 mb-10">
            Let us put our proven method to work for you
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-spire-purple rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-xl"
          >
            Schedule a Consultation
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Preview Badge */}
      <div className="fixed bottom-4 right-4 bg-yellow-500 text-black px-6 py-3 rounded-full font-bold shadow-2xl z-50">
        🎨 PREVIEW MODE
      </div>
    </div>
  );
};

export default PreviewOurMethod;